import {
  Box,
  Button,
  Modal
} from '@platform/ui-library';
import {
  FC,
  useEffect,
  useMemo,
  useState
} from 'react';
import { DesignerSetting } from '@app/DesignerSetting';
import { LoadingSpinner } from '@app/form_components';
import {
  useDesign,
  useLatestDesign,
  useServices
} from '@app/hooks';
import { Design, NotificationType } from '@app/types';
import {
  DesignTtsType,
  filterNull,
  getDesignTtsType,
  getEnvironmentInfo,
  getWfsObject,
  hasLocalChanges
} from '@app/utilities';
import { NotificationMessage } from '../shared';
import styles from './modals.module.scss';

export type SubmitPromptsProps = {
  isOpen: boolean;
  onComplete: () => void;
};

export const SubmitPrompts: FC<SubmitPromptsProps> = ({
  isOpen,
  onComplete
}) => {
  const {
    avsService,
    globalModalsService,
    workflowService
  } = useServices();
  const design = useDesign();
  const latestDesign = useLatestDesign();
  const [designToSubmit, setDesignToSubmit] = useState<Design | null>(null);
  const [loadingMessage, setLoadingMessage] = useState('');

  const ttsType = getDesignTtsType(design);
  const designHasChanges = useMemo((): boolean => {
    return (
      isOpen
      && !!design
      && !!designToSubmit
      && hasLocalChanges({ design, latestDesign: designToSubmit })
    );
  }, [design, isOpen, designToSubmit]);

  // Set `loadingMessage`.
  useEffect(() => {
    if (!designToSubmit) {
      setLoadingMessage('Fetching saved version to submit...');
    } else if (!latestDesign) {
      setLoadingMessage('Getting the latest version...');
    } else {
      setLoadingMessage('');
    }
  }, [designToSubmit, latestDesign]);

  // Set `designToSubmit`.
  useEffect(() => {
    const isVersionMatch = (
      design.branchId === designToSubmit?.branchId
      && design.version === designToSubmit?.version
    );
    if (designToSubmit && !isVersionMatch) {
      setDesignToSubmit(null);
    }

    if (
      !isOpen
      || isVersionMatch
      || !latestDesign
    ) {
      return;
    }

    if (design.version === latestDesign.version) {
      setDesignToSubmit(latestDesign);
    } else {
      (async () => {
        const { branchId, version } = design;
        setDesignToSubmit(await avsService.getDesign(branchId, version));
      })();
    }
  }, [isOpen, design, designToSubmit, latestDesign]);

  const isOnOldVersion = (
    designToSubmit
    && latestDesign
    && designToSubmit.version !== latestDesign.version
  );
  const { isLocal, isProd } = getEnvironmentInfo(DesignerSetting.s.env);
  const isSubmittingFromLocalToProd = isLocal && isProd;

  const warnings = (
    <>
      {(!isOnOldVersion) ? null : (
        <NotificationMessage type={NotificationType.warning}>
          The latest version is (<strong>{latestDesign?.version}</strong>). You should only submit from an old version for hotfixes.
        </NotificationMessage>
      )}
      {(!designHasChanges) ? null : (
        <NotificationMessage type={NotificationType.warning}>
          Please save your local changes before submitting this form.
        </NotificationMessage>
      )}
      {(!isSubmittingFromLocalToProd) ? null : (
        <NotificationMessage type={NotificationType.warning}>
          You are submitting to <strong>PROD</strong> from your <strong>LOCAL</strong> environment.
          Please double-check JSON in console.
        </NotificationMessage>
      )}
    </>
  );

  const onSubmit = async () => {
    if (!designToSubmit || !latestDesign) {
      return;
    }

    const wfsObject = getWfsObject(designToSubmit);
    if (
      designHasChanges
      || isOnOldVersion
      || isSubmittingFromLocalToProd
    ) {
      console.log('WFS object to submit:', wfsObject);
      const proceed = (await globalModalsService.openConfirmationModal({
        children: (
          <div className="box content">
            {warnings}
            <p>Are you sure you wish to continue?</p>
          </div>
        )
      }));
      if (!proceed) {
        return;
      }
    }

    setLoadingMessage('Submitting prompts');
    const result = await workflowService.updatePrompts(wfsObject);
    if (!result?.ok) {
      globalModalsService.openNotificationModal({
        children: 'Please try again or contact an administrator.',
        header: 'Failed to submit form.',
        type: NotificationType.error
      });
    } else {
      const children = filterNull([
        (ttsType === DesignTtsType.ttsOnly) ? null : 'Notifications sent',
        (ttsType === DesignTtsType.voiceOnly) ? null : 'TTS audio generating'
      ]).join(' & ') + '.';
      globalModalsService.openNotificationModal({
        children,
        header: 'Prompts Submitted',
        type: NotificationType.success
      });
    }
    setLoadingMessage('');
    onComplete();
  };

  return (
    <>
      <Modal isOpen={isOpen}>
        <Box theme="noPadding" className={styles.container}>
          <div className="box header">
            <h2>Submit Prompts</h2>
          </div>
          <div className="box content">
            {(!designToSubmit || !latestDesign) ? null : (
              getFormDescription(
                ttsType,
                designToSubmit.version
              )
            )}
            {warnings}
            {loadingMessage && (
              <LoadingSpinner
                message={loadingMessage}
                hideSpinner={!loadingMessage}
                className={styles.loadingSpinner}
              />
            )}
          </div>
          <div className="box footer">
            <Button
              onClick={onComplete}
              theme="outline"
              type="button"
            >
              Cancel
            </Button>
            <Button onClick={onSubmit} disabled={!!loadingMessage}>
              Submit
            </Button>
          </div>
        </Box>
      </Modal>
    </>
  );
};

function getFormDescription(
  ttsType: DesignTtsType,
  version: string
) {
  return (ttsType === DesignTtsType.ttsOnly) ? (
    <>
      <p>
        This form will use the prompts in the saved version (<strong>{version}</strong>) to generate TTS audio.
      </p>
    </>
  ) : (
    <>
      <p>
        {'This form will send notifications to the app\'s Voice Talents, asking them to record audio for prompts that have been added or modified as of the saved version'}
        (<strong>{version}</strong>)
        {'. '}
      </p>
      <p>For languages that have a TTS voice selected instead, TTS audio will be generated.</p>
    </>
  );
}
